import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
// import LoadingOverlay from "react-loading-overlay-ts";

// MUI imports
import { Box, Grid, Typography } from "@mui/material";

// Project Imports
import TextFieldPA from "./UI/TextFieldPA";
import FormSubmitButton from "./UI/FormSubmitButton";
import ApprovalGroup from "./UI/ApprovalGroup";
import { components } from "../../Components/Icons/DataTableIcons";
import {
  generateEquipmentTableData,
  generateStaffCompositionTableData,
} from "./helpers";

import { paNextFormStep } from "../../Actions";
// import { Button } from "@mui/material";

const ColumnTextStyle = {
  textTransform: "capitalize",
  textAlign: "left",
  color: "#212b36",
  fontSize: "0.875rem",
  fontWeight: 600,
};

const CustomTableColumnHeaders = ({ index, column }) => {
  const headStyle = {};

  if (column.label === "First Name") {
    headStyle.paddingLeft = "24px";
  }
  return (
    <Typography style={headStyle} sx={ColumnTextStyle} key={index}>
      {column.label}
    </Typography>
  );
};

const CustomDatatableTitle = ({ title }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={10}
    justifyContent="space-between"
  >
    <Typography sx={{ color: "#05004E", fontWeight: 600 }}>{title}</Typography>
  </Box>
);

const paColumnOptions = {
  customHeadLabelRender: ({ index, ...column }) => {
    return <CustomTableColumnHeaders index={index} column={column} />;
  },
};

const formValidationSchema = Yup.object().shape({
  isTrainers: Yup.boolean().required("This is a required field"),
  isDressingWell: Yup.boolean().required("This is a required field"),
  isSkillCenterEquipments: Yup.boolean().required("This is a required field"),
  isCenterCapacity: Yup.boolean().required("This is a required field"),
  isCommentOnConduciveness: Yup.boolean().required("This is a required field"),
});

const formState = {
  isTrainers: null,
  isDressingWell: null,
  isSkillCenterEquipments: null,
  isCenterCapacity: null,
  isCommentOnConduciveness: null,
};

const PartTwo = () => {
  const globalDispatch = useDispatch();
  const tregPhysicalApprovalReducer = (globalState) =>
    globalState.tregPhysicalApprovalReducer;

  const { singleTrainerDetails } = useSelector(tregPhysicalApprovalReducer);

  const [staffTableData, setStaffTableData] = useState([]);
  const [equipmentTableData, setEquipmentTableData] = useState([]);

  useEffect(() => {
    // Generate staff composition table data
    if (Object.keys(singleTrainerDetails).length > 0) {
      const staffData = generateStaffCompositionTableData(singleTrainerDetails);
      const equipmentData = generateEquipmentTableData(singleTrainerDetails);

      setStaffTableData(staffData);
      setEquipmentTableData(equipmentData);
    }
  }, [singleTrainerDetails]);

  const staffDataColumns = [
    {
      name: "firstName",
      label: "First Name",
      options: {
        ...paColumnOptions,
        setCellProps: () => ({
          style: { paddingLeft: "24px" },
        }),
      },
    },
    {
      name: "middleName",
      label: "Middle Name",
      options: paColumnOptions,
    },
    {
      name: "lastName",
      label: "Last Name",
      options: paColumnOptions,
    },
    {
      name: "yearsOfExperience",
      label: "Years Of Experience",
      options: paColumnOptions,
    },
    {
      name: "skill",
      label: "Skill",
      options: paColumnOptions,
    },
    {
      name: "certifications",
      label: "Number of Certifications",
      options: paColumnOptions,
    },
  ];

  const equipmentDataColumns = [
    {
      name: "equipmentName",
      label: "Equipment Name",
      options: {
        ...paColumnOptions,
        setCellProps: () => ({
          style: { paddingLeft: "24px" },
        }),
      },
    },
    {
      name: "equipmentUnits",
      label: "Equipment Units",
      options: paColumnOptions,
    },
    {
      name: "remark",
      label: "Remark",
      options: paColumnOptions,
    },
    {
      name: "skill",
      label: "Skill",
      options: paColumnOptions,
    },
  ];

  const staffDataOptions = {
    filter: false,
    // count: tablePaginationData.totalCount,
    disableRipple: true,
    elevation: 0,
    fixedHeader: true,
    print: false,
    responsive: "standard",
    rowsPerPage: 10,
    rowsPerPageOptions: [10],
    search: false,
    selectableRows: "none",
    serverSide: true,
  };

  const sectionFourMarkup = [
    {
      name: "isCenterCapacity",
      value: singleTrainerDetails.centerCapacity,
      label:
        "1. How many trainees can the centre comfortably take per training session?*",
    },
    {
      label:
        "2. Do you think the training centre is located in a conducive environment (emphasis on hygiene, security, space and ventilation) for the skill you are teaching? If yes, state justification. If no, state reason.*",
      name: "isCommentOnConduciveness",
      value: singleTrainerDetails.commentOnConduciveness,
    },
  ];

  function handleFormSubmit(values) {
    debugger;
    globalDispatch(paNextFormStep(values))
  }

  // function handleBack() {
  //   globalDispatch(paPreviousFormStep())
  // }

  return (
    <Formik
      initialValues={{ ...formState }}
      validationSchema={formValidationSchema}
      onSubmit={handleFormSubmit}
    >
      <Form>
        <h2>Section 2</h2>

        {/* Staff Composition */}
        <div>
          <MUIDataTable
            title={<CustomDatatableTitle title={"Staff Composition"} />}
            columns={staffDataColumns}
            components={components}
            options={staffDataOptions}
            data={staffTableData}
          />
          <ApprovalGroup fieldName={"isTrainers"} />
        </div>

        {/* 2.ii */}
        <div>
          <p>
            ii.Do the trainers dress in accordance with the professional
            standard of the skill area they offer training in?
          </p>
          <ApprovalGroup fieldName={"isDressingWell"} />
        </div>

        <h2>Section 3</h2>

        {/* Training Equipments */}
        <div>
          <MUIDataTable
            title={<CustomDatatableTitle title={"Training Equipments"} />}
            columns={equipmentDataColumns}
            components={components}
            options={staffDataOptions}
            data={equipmentTableData}
          />
          <ApprovalGroup fieldName={"isSkillCenterEquipments"} />
        </div>

        <h2> Section 4</h2>

        {sectionFourMarkup.map((field) => (
          <div key={field.label}>
            <TextFieldPA label={field.label} value={field.value} />
            <ApprovalGroup fieldName={field.name} />
          </div>
        ))}

        <Grid
          item
          xs
          container
          sx={{ justifyContent: "flex-end", paddingTop: "1rem" }}
        >
          {/* <Button
            variant={"outlined"}
            onClick={handleBack}
          >Back</Button> */}
          <FormSubmitButton sx={{ width: "fit-content" }}>
            Next
          </FormSubmitButton>
        </Grid>
      </Form>
    </Formik>
  );
};

export default PartTwo;
